import { Injectable } from '@angular/core';
import { Either, Left, Right } from 'purify-ts';
import { ConversationsRepositoryFailure } from '../../domain/conversations/failure';
import { IConversationsRepository } from '../../domain/conversations/i-conversations-repository';
import {
  ContactName,
  Conversation,
  ListConversationsOptions,
  ListMessagesOptions,
  MarkConversationAsReadProps,
  MarkedAsRead,
  Message,
  MessageContent,
  Paginated,
  SendMessageProps,
  SendTemplateMessage,
  SentMessage,
  UpdateContactNameProps,
  buildMessagePreview,
  isWindowOpenFromMessages,
  windowCloseAtFromMessages,
} from '../../domain/conversations/types';
import {
  FakeConversationSeed,
  fakeConversations,
  fakeNextMessageId,
  fakeTemplateSnapshot,
  fakeTemplateText,
} from './data';

@Injectable({
  providedIn: 'root',
})
export class FakeConversationsRepository implements IConversationsRepository {
  private seeds: FakeConversationSeed[] = fakeConversations.map(seed => ({
    ...seed,
    messages: [...seed.messages],
  }));

  public async getConversations(
    _options: ListConversationsOptions
  ): Promise<Either<ConversationsRepositoryFailure, Paginated<Conversation>>> {
    const items: Conversation[] = [...this.seeds]
      .map(seed => {
        const last = seed.messages[seed.messages.length - 1];
        const windowCloseAt = windowCloseAtFromMessages(seed.messages);

        return {
          phoneNumber: seed.phoneNumber,
          name: seed.name,
          chosenName: seed.chosenName ?? null,
          lastMessageAt: last.messageAt,
          lastMessagePreview: buildMessagePreview(last),
          lastMessageDirection: last.direction,
          lastInboundAt: [...seed.messages]
            .reverse()
            .find(message => message.direction === 'INBOUND')?.messageAt,
          messageWindowCloseAt: windowCloseAt ? windowCloseAt.toISOString() : null,
          unreadCount: this.unreadOf(seed),
        };
      })
      .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));

    return Right({ items, nextCursor: null });
  }

  public async getMessages(
    options: ListMessagesOptions
  ): Promise<Either<ConversationsRepositoryFailure, Paginated<Message>>> {
    const seed = this.findSeed(options.clientPhoneNumber);

    if (!seed) {
      return Right({ items: [], nextCursor: null });
    }

    return Right({ items: [...seed.messages].reverse(), nextCursor: null });
  }

  public async sendMessage(
    props: SendMessageProps
  ): Promise<Either<ConversationsRepositoryFailure, SentMessage>> {
    const seed = this.findSeed(props.clientPhoneNumber);

    if (!seed) {
      return Left(ConversationsRepositoryFailure.failToComunicateWithTheServer());
    }

    if (props.type === 'TEXT' && !isWindowOpenFromMessages(seed.messages)) {
      return Left(ConversationsRepositoryFailure.outOfConversationWindow());
    }

    const message: Message =
      props.type === 'TEXT'
        ? {
            id: fakeNextMessageId(),
            direction: 'OUTBOUND',
            type: 'TEXT',
            content: { text: props.content.text },
            status: 'ACCEPTED',
            messageAt: new Date().toISOString(),
          }
        : props.type === 'TEMPLATE'
          ? {
              id: fakeNextMessageId(),
              direction: 'OUTBOUND',
              type: 'TEMPLATE',
              content: this.buildTemplateContent(props.content),
              status: 'ACCEPTED',
              messageAt: new Date().toISOString(),
            }
          : {
              id: fakeNextMessageId(),
              direction: 'OUTBOUND',
              type: props.type,
              content: {
                media: {
                  mediaId: props.content.mediaId,
                  fileName: props.content.fileName,
                  caption: props.content.caption,
                },
              },
              status: 'ACCEPTED',
              messageAt: new Date().toISOString(),
            };

    seed.messages.push(message);

    return Right({ id: message.id, wamid: `wamid.FAKE${message.id}`, status: 'ACCEPTED' });
  }

  public async markConversationAsRead(
    props: MarkConversationAsReadProps
  ): Promise<Either<ConversationsRepositoryFailure, MarkedAsRead>> {
    const seed = this.findSeed(props.clientPhoneNumber);

    if (!seed) {
      return Left(ConversationsRepositoryFailure.failToComunicateWithTheServer());
    }

    const markedCount = this.unreadOf(seed);
    const readAt = new Date().toISOString();

    seed.readAt = readAt;

    return Right({
      companyPhoneNumber: props.companyPhoneNumber,
      clientPhoneNumber: props.clientPhoneNumber,
      markedCount,
      readAt: markedCount > 0 ? readAt : null,
    });
  }

  public async updateContactName(
    props: UpdateContactNameProps
  ): Promise<Either<ConversationsRepositoryFailure, ContactName>> {
    const seed = this.findSeed(props.clientPhoneNumber);

    if (!seed) {
      return Left(ConversationsRepositoryFailure.failToComunicateWithTheServer());
    }

    seed.chosenName = props.chosenName.trim() || null;

    return Right({
      phoneNumber: seed.phoneNumber,
      name: seed.name,
      chosenName: seed.chosenName,
    });
  }

  private buildTemplateContent(content: SendTemplateMessage['content']): MessageContent {
    const template = fakeTemplateSnapshot(content.templateName, content.variables);

    return {
      templateName: content.templateName,
      templateLanguage: content.language,
      templateVariables: content.variables,
      ...(template ? { template, text: fakeTemplateText(template) } : {}),
    };
  }

  private unreadOf(seed: FakeConversationSeed): number {
    return seed.messages.filter(
      message =>
        message.direction === 'INBOUND' && (!seed.readAt || message.messageAt > seed.readAt)
    ).length;
  }

  private findSeed(clientPhoneNumber: string): FakeConversationSeed | undefined {
    return this.seeds.find(seed => seed.phoneNumber === clientPhoneNumber);
  }
}
